import { onMount, onCleanup, createSignal, createEffect, untrack } from "solid-js";
import * as d3 from "d3";
import { defaultChannelsStore } from "../../store/defaultChannelsStore";
import {
  resolveDataField,
  EMPTY_GROUP_COLOR,
  isEmptyGroupValue,
  isRaceColorMode,
  isTrainingRaceMetadataValue,
  RACE_NUMBER_COLLAPSED_LEGEND_NAME,
} from "../../utils/colorScale";
import { setSelectedEvents, setSelectedAggregateEvents, setHasSelection, setTriggerSelection, setLegendSelectionActive, selectedEvents, selectedAggregateEvents, highlightedEventIds, highlightedAggregateEventIds, legendSelectionActive, type SelectionDomain } from "../../store/selectionStore";
import { isDark } from "../../store/themeStore";
import { debug as logDebug } from "../../utils/console";
import { getCurrentDatasetTimezone, setCurrentDataset } from "../../store/datasetTimezoneStore";
import { persistantStore } from "../../store/persistantStore";
import { selectedSources } from "../../store/filterStore";
import { getDimensionItemColorGetter } from "../../utils/colorGrouping";
import { resolveItemColor, UNSELECTED_TRACK_COLOR } from "../../utils/selectionColors";
import {
  parseScatterDatetime,
  type ScatterDatetimeCache,
} from "../../utils/maneuverScatterTimeseries";
import { sampleTimelinePointsForRender } from "../../utils/scatterRenderSample";
import { PERFORMANCE_DENSITY_COLOR, PERFORMANCE_VMG_COLOR } from "../../utils/colorScale";
import { densityHeatColor, vmgHeatColor } from "../../utils/scatterColors";
import { pointDensityFractions } from "../../utils/scatterDensity";
import { vmgFractions, vmgPercFromRow } from "../../utils/scatterVmgPerc";

/** Color By DENSITY cell (px) for the strip's own time × BSP points; smaller than the scatter's 14 (the strip is short). */
const TIMELINE_DENSITY_CELL_PX = 8;
import { addScatterBrushRange, type ScatterBrushRange } from "../../utils/scatterBrushRanges";
import { formatDate, formatTime } from "../../utils/global";

/** Match AdvancedScatter scatter DOM cap for performance timelines. */
const TIMELINE_MAX_RENDER_POINTS = 3200;

type ProcessedScatterPoint = Record<string, unknown> & {
  datetime: Date;
  Datetime: Date;
  event_id: unknown;
  BSP?: number;
  TWS?: number;
};

interface ScatterTimeseriesProps {
  /**
   * Which selection domain this strip reads and writes. Defaults to `'maneuver'`.
   *
   * This component is hosted by BOTH families — the maneuver pages and the performance pages — which
   * is why it needs to be told. Before the domains were split it could only guess, and the guess is
   * what `applyTimelineSelectionDimming` was working around when it refused to dim at all because
   * "Performance/other pages leave stale IDs unrelated to fleet rows".
   */
  selectionDomain?: SelectionDomain;
  key?: string;  // Solid.js key for control flow (re-mount when key changes)
  aggregates: any[];  // Array of aggregate data points
  color: string;      // Field name for coloring (e.g., 'TACK', 'GRADE', 'SOURCE_NAME')
  groups: Array<{ name: string; color: string }>;  // Color groups from parent
  colorScale?: (value: string) => string;  // Optional color scale for fleet components
  isHistoryPage?: boolean;  // Whether this is a history page (affects x-axis formatting)
  uwDw?: string;  // Upwind/Downwind filter: 'UPWIND' or 'DOWNWIND'
  showUwDw?: boolean;  // Whether to apply upwind/downwind filtering
  /** When true, do not filter points by filterStore selectedSources (maneuver pages use local source selection; store list can exclude all rows). */
  skipSelectedSourcesFilter?: boolean;
  /**
   * Colour every point from `color`/`groups` alone, ignoring the selection.
   *
   * `skipSelectedSourcesFilter` otherwise opts a strip into the maneuver-selection colour model,
   * where a brush repaints points with per-index colours and greys the rest. That is right when the
   * strip IS the selection UI; it is wrong when the colour carries its own meaning (the live
   * CALIBRATION strip colours by tack, and a brush there only sets a time window). Selection is
   * still shown via opacity.
   */
  colorByFieldOnly?: boolean;
  /** Optional callback when brush selection changes (e.g. Summary Tables local time-range filter). */
  onBrushSelect?: (detail: {
    eventIds: number[];
    startTime: Date | null;
    endTime: Date | null;
  }) => void;
}

export default function ScatterTimeseries(props: ScatterTimeseriesProps) {
  /**
   * Domain-scoped selection access. `'maneuver'` (the default) resolves to exactly the calls this
   * component made before the split, so nothing changes for the maneuver hosts.
   */
  const isAggregateDomain = (): boolean => props.selectionDomain === 'aggregate';
  /** The manual selection this strip ACTS on — brush writes, legend toggles. */
  const domainSelectedEvents = (): number[] =>
    isAggregateDomain() ? selectedAggregateEvents() : selectedEvents();
  const setDomainSelectedEvents = (value: number[]): void => {
    if (isAggregateDomain()) { setSelectedAggregateEvents(value); return; }
    setSelectedEvents(value);
  };
  /** What a point should LOOK like: manual selection union the live auto-follow highlight. */
  const domainHighlightedIds = (): number[] =>
    isAggregateDomain() ? highlightedAggregateEventIds() : highlightedEventIds();

  let containerRef: HTMLElement | null = null;
  const [width, setWidth] = createSignal(0);
  /** Match `.scatter-timeseries-root` / `.maneuver-selection-timeline` strip (performance history uses 88px). */
  const [height] = createSignal(88);

  const margin = { top: 5, right: 10, bottom: 20, left: 40 };
  let svg: d3.Selection<SVGSVGElement, unknown, null, undefined> | null = null;
  let xScale: d3.ScaleTime<number, number, never> | null = null;
  let yScale: d3.ScaleLinear<number, number, never> | null = null;
  let brush: d3.BrushBehavior<unknown> | null = null;
  let resizeObserver: ResizeObserver | null = null;
  let resizeObservedParent: HTMLElement | null = null;
  let widthRafId: number | null = null;

  // Track signatures for change detection (like AdvancedScatter)
  let lastDataSignature = '';
  let lastColorGroupsSignature = '';
  let lastTimezoneForAxis = '';
  let dataRedrawTimer: ReturnType<typeof setTimeout> | null = null;
  let resizeRedrawTimer: ReturnType<typeof setTimeout> | null = null;
  let brushPublishTimer: ReturnType<typeof setTimeout> | null = null;
  /** Ctrl (or Cmd) held when the drag started: add this band to the selection instead of replacing it. */
  let additiveGestureArmed = false;
  /** Set while we re-seat the brush ourselves, so our own `brush.move` does not re-enter `brushed`. */
  let isProgrammaticBrushMove = false;
  /** Bands accumulated by Ctrl+drag; a plain drag resets this to the single band just drawn. */
  let accumulatedRanges: ScatterBrushRange[] = [];
  /**
   * Event ids behind `accumulatedRanges`. Held locally rather than read back from `domainSelectedEvents()`
   * because `publishBrushSelection` debounces the store write by 50ms — a quick second Ctrl+drag would
   * otherwise union against a stale value and silently drop the first band.
   */
  let accumulatedEventIds = new Set<number>();
  let maneuverDimensionColorFn: ((item: Record<string, unknown>) => string) | null = null;
  let lastProcessedData: ProcessedScatterPoint[] = [];
  let drawDatetimeCache: ScatterDatetimeCache = new Map();
  let drawChartCallCount = 0;
  let drawChartMountMs = 0;

  const { selectedClassName, selectedProjectId, selectedDatasetId } = persistantStore;

  // Get channel names (non-reactive)
  const getBspName = () => defaultChannelsStore.bspName() || 'Bsp_kph';
  const getTwsName = () => defaultChannelsStore.twsName() || 'Tws_kts';
  const getTwaName = () => defaultChannelsStore.twaName() || 'Twa_deg';

  // Helper function to get timezone (non-reactive, called when needed)
  const getTimezone = (): string | null => {
    return getCurrentDatasetTimezone();
  };

  // Helper function to create a time formatter with timezone support (for labels only)
  const createTimeFormatter = (timezone: string | null, isHistory: boolean) => {
    return (date: Date | d3.NumberValue) => {
      // D3 passes dates as d3.NumberValue, convert to Date if needed
      const dateObj = date instanceof Date ? date : new Date(date as number);

      if (!dateObj || isNaN(dateObj.getTime())) {
        return '';
      }

      try {
        if (isHistory) {
          return formatDate(dateObj, timezone) ?? d3.timeFormat("%Y-%m-%d")(dateObj);
        }
        return formatTime(dateObj, timezone) ?? formatTime(dateObj) ?? d3.timeFormat("%H:%M:%S")(dateObj);
      } catch (error) {
        logDebug('ScatterTimeseries: Error formatting time with timezone', { timezone, error, date: dateObj });
        if (isHistory) {
          return d3.timeFormat("%Y-%m-%d")(dateObj);
        }
        return d3.timeFormat("%H:%M:%S")(dateObj);
      }
    };
  };

  // Color function matching PerfScatter/FleetScatter pattern
  const getColor = (d: any, color?: string, groups?: Array<{ name: string; color: string }>, colorScale?: (value: string) => string): string => {
    const currentColor = color ?? props.color;
    const currentGroups = groups ?? props.groups;
    const currentColorScale = colorScale ?? props.colorScale;

    // If colorScale is provided AND color is SOURCE (maneuvers) or SOURCE_NAME (performance), use colorScale
    if ((currentColor === 'SOURCE_NAME' || currentColor === 'SOURCE') && currentColorScale && typeof currentColorScale === 'function') {
      // Get source name from data (check multiple field name variations)
      const sourceName = d.source_name || d.sourceName || d.source || d.SOURCE || d.SOURCE_NAME;
      if (sourceName) {
        const normalizedSource = String(sourceName).toLowerCase();
        return currentColorScale(normalizedSource);
      }
    }

    // If color prop is provided and groups are available, use them
    if (currentColor && currentGroups && Array.isArray(currentGroups)) {
      // Use resolveDataField to get the value
      let value = resolveDataField(d, currentColor);

      // Special handling for race number: convert -1 to 'TRAINING'
      if (isRaceColorMode(currentColor) && (value === -1 || value === '-1')) {
        value = 'TRAINING';
      }

      if (isRaceColorMode(currentColor) && currentGroups) {
        const hasRacingBucket = currentGroups.some(
          (g) => String(g.name) === RACE_NUMBER_COLLAPSED_LEGEND_NAME
        );
        if (hasRacingBucket) {
          if (isTrainingRaceMetadataValue(value)) {
            const tg = currentGroups.find((g) => String(g.name) === 'TRAINING');
            return tg ? tg.color : 'lightgrey';
          }
          const rg = currentGroups.find((g) => String(g.name) === RACE_NUMBER_COLLAPSED_LEGEND_NAME);
          return rg ? rg.color : 'lightgrey';
        }
      }

      // When colored by LEG, training / -1 always use light grey (match legend and AdvancedScatter)
      if (currentColor === 'LEG') {
        const note = value !== undefined && value !== null ? String(value) : '';
        if (note === 'TRAINING' || note === 'training' || note === '-1' || value === -1) {
          return 'lightgrey';
        }
      }

      // Missing and NONE use the same grey as the legend swatch, not D3's first category blue.
      if (isEmptyGroupValue(value)) {
        return EMPTY_GROUP_COLOR;
      }
      // Convert value to string for consistent comparison
      const valueStr = value !== undefined && value !== null ? String(value) : null;

      // Find matching group - handle both string and number comparisons
      let group = null;
      if (valueStr !== null) {
        group = currentGroups.find(group => {
          // Try exact string match first
          if (String(group.name) === valueStr) return true;
          // Try number comparison if both are numeric
          const groupNum = Number(group.name);
          const valueNum = Number(valueStr);
          if (!isNaN(groupNum) && !isNaN(valueNum) && groupNum === valueNum) return true;
          // Try case-insensitive string match
          if (String(group.name).toLowerCase() === valueStr.toLowerCase()) return true;
          return false;
        });
      }

      return group ? group.color : "lightgrey";
    }

    // Fallback: return lightgrey if no color/groups provided
    return "lightgrey";
  };

  const refreshManeuverDimensionColorFn = () => {
    if (!props.skipSelectedSourcesFilter || props.colorByFieldOnly) {
      maneuverDimensionColorFn = null;
      return;
    }
    const colorMode = props.color || 'TWS';
    const aggregates = props.aggregates;
    const rows = Array.isArray(aggregates) ? (aggregates as Record<string, unknown>[]) : [];
    maneuverDimensionColorFn = getDimensionItemColorGetter(rows, colorMode);
  };

  /** Maneuver selection strip: dimension + index colors via shared resolver; performance timelines keep group lookup. */
  const getScatterPointColor = (
    d: Record<string, unknown>,
    color?: string,
    groups?: Array<{ name: string; color: string }>,
    colorScale?: (value: string) => string
  ): string => {
    if (props.skipSelectedSourcesFilter && !props.colorByFieldOnly) {
      const colorMode = color ?? props.color ?? 'TWS';
      return resolveItemColor(d, {
        selectedIds: domainSelectedEvents(),
        legendSelectionActive: legendSelectionActive(),
        colorMode,
        getDimensionColor: (item) => {
          if (maneuverDimensionColorFn) {
            return maneuverDimensionColorFn(item as Record<string, unknown>);
          }
          return getColor(item, colorMode, groups, colorScale);
        },
        unselectedColor: UNSELECTED_TRACK_COLOR,
      });
    }
    return getColor(d, color, groups, colorScale);
  };

  /**
   * Color By DENSITY / VMG: the strip paints its own ramp (density of its time × BSP pixels, or VMG %
   * placed on its own 2nd–98th percentile range) instead of a group lookup, and sorts the hottest on
   * top. Returns false for any other color-by, leaving the caller's group colouring in place.
   */
  const paintRampColors = (
    circles: d3.Selection<d3.BaseType, unknown, d3.BaseType, unknown>,
    color: string | undefined,
    animate: boolean
  ): boolean => {
    if (color !== PERFORMANCE_DENSITY_COLOR && color !== PERFORMANCE_VMG_COLOR) {return false;}
    const rows = circles.data() as Array<Record<string, unknown>>;
    let heat: (d: unknown) => number;
    let colorOf: (d: unknown) => string;
    if (color === PERFORMANCE_DENSITY_COLOR) {
      const fractions = pointDensityFractions(
        rows.map((d) => ({
          x: Number(xScale?.((d.datetime || d.Datetime) as Date)),
          y: Number(yScale?.(d.BSP as number)),
        })),
        TIMELINE_DENSITY_CELL_PX
      );
      const byRow = new Map<unknown, number>();
      rows.forEach((d, i) => byRow.set(d, fractions[i] ?? 0));
      heat = (d) => byRow.get(d) ?? 0;
      colorOf = (d) => densityHeatColor(heat(d));
    } else {
      const fractions = vmgFractions(rows, vmgPercFromRow);
      heat = (d) => fractions.get(d as Record<string, unknown>) ?? -1;
      colorOf = (d) => {
        const f = fractions.get(d as Record<string, unknown>);
        return f === undefined ? 'lightgrey' : vmgHeatColor(f);
      };
    }
    circles.sort((a, b) => heat(a) - heat(b));
    const target = animate ? circles.transition().duration(200) : circles;
    (target as typeof circles).style("fill", colorOf).style("stroke", colorOf);
    return true;
  };

  const applyManeuverStripSelectionOpacity = () => {
    if (!props.skipSelectedSourcesFilter || !svg?.node()) return;
    const sel = domainSelectedEvents();
    const hasSel = Array.isArray(sel) && sel.length > 0;
    const chartBody = svg.select("g");
    if (chartBody.empty()) return;
    chartBody.selectAll("circle.scatter-point").each(function (d: Record<string, unknown>) {
      const rawId = d?.event_id;
      if (rawId === undefined || rawId === null || rawId === '') {
        d3.select(this).style("opacity", hasSel ? 0.25 : 1);
        return;
      }
      const id = typeof rawId === 'string' ? parseInt(rawId, 10) : Number(rawId);
      const inSel = hasSel && !isNaN(id) && sel.includes(id);
      d3.select(this).style("opacity", hasSel ? (inSel ? 1 : 0.35) : 1);
    });
  };

  // Process data: filter and prepare for rendering (single pass; parse datetime once per row)
  const processData = (data: any[], datetimeCache: ScatterDatetimeCache): ProcessedScatterPoint[] => {
    if (!data || data.length === 0) return [];

    const bspFieldName = getBspName();
    const bspFieldNameLower = bspFieldName.toLowerCase();
    const twsFieldName = getTwsName();
    const twsFieldNameLower = twsFieldName.toLowerCase();
    const twaFieldName = getTwaName();

    // Get selected sources and normalize to lowercase for comparison (maneuver timelines skip this)
    const selected = props.skipSelectedSourcesFilter ? [] : selectedSources();
    const selectedSourcesLower = selected.length > 0
      ? selected.map(s => String(s).toLowerCase().trim())
      : [];
    /** Maneuver strips receive pre-mapped UTC ISO rows from `mapManeuverRowsToScatterTimeseriesPoints`. */
    const maneuverStrip = !!props.skipSelectedSourcesFilter;

    const out: ProcessedScatterPoint[] = [];

    for (const d of data) {
      const row = d as Record<string, unknown>;

      // Filter by selected sources if any are selected (skip for maneuver selection timelines)
      if (!props.skipSelectedSourcesFilter && selectedSourcesLower.length > 0) {
        const sourceName =
          row.source_name ||
          row.sourceName ||
          row.source ||
          row.SOURCE ||
          row.SOURCE_NAME ||
          row.Source_name ||
          '';
        const normalizedSourceName = String(sourceName).toLowerCase().trim();
        if (!normalizedSourceName || !selectedSourcesLower.includes(normalizedSourceName)) {
          continue;
        }
      }

      const datetime = parseScatterDatetime(row, datetimeCache);
      if (!datetime) continue;

      const hasBsp =
        (row[bspFieldName] !== undefined && row[bspFieldName] !== null) ||
        (row[bspFieldNameLower] !== undefined && row[bspFieldNameLower] !== null) ||
        (row.BSP !== undefined && row.BSP !== null) ||
        (row.Bsp_kph !== undefined && row.Bsp_kph !== null) ||
        (row.bsp_kph !== undefined && row.bsp_kph !== null) ||
        (row.Bsp_kts !== undefined && row.Bsp_kts !== null) ||
        (row.bsp_kts !== undefined && row.bsp_kts !== null);
      const hasTws =
        (row[twsFieldName] !== undefined && row[twsFieldName] !== null) ||
        (row[twsFieldNameLower] !== undefined && row[twsFieldNameLower] !== null) ||
        (row.TWS !== undefined && row.TWS !== null) ||
        (row.Tws_kts !== undefined && row.Tws_kts !== null) ||
        (row.tws_kts !== undefined && row.tws_kts !== null) ||
        (row.Tws_kph !== undefined && row.Tws_kph !== null) ||
        (row.tws_kph !== undefined && row.tws_kph !== null) ||
        (row.Tws_avg !== undefined && row.Tws_avg !== null) ||
        (row.tws_avg !== undefined && row.tws_avg !== null) ||
        (row.Tws_bin !== undefined && row.Tws_bin !== null) ||
        (row.tws_bin !== undefined && row.tws_bin !== null);
      if ((maneuverStrip && !hasTws) || (!maneuverStrip && !hasBsp)) continue;

      // Apply upwind/downwind filtering if enabled
      if (props.showUwDw && props.uwDw) {
        const twaAvg = row.Twa_deg_avg ?? row.twa_deg_avg;
        if (twaAvg !== undefined && twaAvg !== null && !isNaN(Number(twaAvg))) {
          if (props.uwDw === 'UPWIND') {
            if (Math.abs(Number(twaAvg)) >= 90) continue;
          } else if (Math.abs(Number(twaAvg)) < 90) {
            continue;
          }
        } else {
          const twaValue = row[twaFieldName] ?? row.Twa ?? row.twa ?? row.Twa_deg ?? row.twa_deg;
          if (twaValue !== undefined && twaValue !== null && !isNaN(Number(twaValue))) {
            if (props.uwDw === 'UPWIND') {
              if (Math.abs(Number(twaValue)) >= 90) continue;
            } else if (Math.abs(Number(twaValue)) < 90) {
              continue;
            }
          }
        }
      }

      if (maneuverStrip) {
        const twsValue =
          row[twsFieldName] ??
          row[twsFieldNameLower] ??
          row.TWS ??
          row.Tws_kts ??
          row.tws_kts ??
          row.Tws_kph ??
          row.tws_kph ??
          row.Tws_avg ??
          row.tws_avg ??
          row.Tws_bin ??
          row.tws_bin;
        const twsNum = Number(twsValue);
        if (!Number.isFinite(twsNum)) {
          logDebug('ScatterTimeseries: Skipping maneuver row with non-finite TWS', {
            event_id: row.event_id,
          });
          continue;
        }
        out.push({
          ...row,
          datetime,
          Datetime: datetime,
          TWS: twsNum,
          event_id: row.event_id,
        });
        continue;
      }

      const bspValue =
        row[bspFieldName] ??
        row[bspFieldNameLower] ??
        row.BSP ??
        row.Bsp_kph ??
        row.bsp_kph ??
        row.Bsp_kts ??
        row.bsp_kts ??
        0;
      const bspNum = Number(bspValue) || 0;

      out.push({
        ...row,
        datetime,
        Datetime: datetime,
        BSP: bspNum,
        event_id: row.event_id,
      });
    }

    return out;
  };

  /** Performance dataset timelines only — maneuver strips may need broader layout observers. */
  const isPerformanceTimeline = (): boolean => !props.skipSelectedSourcesFilter;

  const notifyBrushSelect = (
    eventIds: number[],
    startTime: Date | null,
    endTime: Date | null
  ) => {
    props.onBrushSelect?.({ eventIds, startTime, endTime });
  };

  const clearBrushSelectionVisual = () => {
    if (!brush || !svg?.node()) {
      return;
    }
    svg.select<SVGGElement>(".brush").call(brush.move, null);
  };

  /** Forget every accumulated Ctrl+drag band (the selection was cleared from somewhere else). */
  const resetAccumulatedBrushBands = () => {
    accumulatedRanges = [];
    accumulatedEventIds = new Set<number>();
    renderAccumulatedBrushBands();
  };

  /**
   * Draw the accumulated Ctrl+drag bands. With a single band the interactive D3 rectangle already shows
   * it; from two bands on, the D3 rectangle is dropped (it can only show one) and every band is painted
   * here in the same ink so the strip still reads as one selection.
   */
  function renderAccumulatedBrushBands(): void {
    if (!svg?.node() || !xScale) {
      return;
    }
    const chartBody = svg.select("g");
    if (chartBody.empty()) {
      return;
    }
    const bandGroup = chartBody.select<SVGGElement>("g.brush-bands");
    if (bandGroup.empty()) {
      return;
    }

    const bands = accumulatedRanges.length > 1
      ? accumulatedRanges
        .map((range) => {
          const x0 = xScale?.(new Date(range.start_time));
          const x1 = xScale?.(new Date(range.end_time));
          if (!Number.isFinite(x0) || !Number.isFinite(x1)) {
            return null;
          }
          return {
            x: Math.min(x0 as number, x1 as number),
            width: Math.abs((x1 as number) - (x0 as number)),
          };
        })
        .filter((band): band is { x: number; width: number } => band !== null)
      : [];

    // Few bands, and they change only on a drag — redraw rather than keep a data join alive.
    const chartHeight = Math.max(height() - margin.top - margin.bottom, 0);
    bandGroup.selectAll("rect.brush-band").remove();
    for (const band of bands) {
      bandGroup.append("rect")
        .attr("class", "brush-band")
        .attr("x", band.x)
        .attr("y", 0)
        .attr("width", Math.max(band.width, 1))
        .attr("height", chartHeight)
        // Same ink as D3's own brush rectangle, so a multi-band selection reads as one selection.
        .style("fill", "#777")
        .style("fill-opacity", 0.3)
        .style("stroke", "#fff")
        .style("shape-rendering", "crispEdges");
    }
  }

  /** Re-seat the single D3 rectangle on the accumulated bands (or drop it once there is more than one). */
  function restoreBrushSelection(): void {
    if (!brush || !xScale || !svg?.node()) {
      return;
    }
    const chartBody = svg.select("g");
    const brushGroup = chartBody.select<SVGGElement>("g.brush");
    if (brushGroup.empty()) {
      return;
    }

    isProgrammaticBrushMove = true;
    try {
      // Two or more accumulated bands: nothing for the single D3 rectangle to represent.
      if (accumulatedRanges.length > 1) {
        brushGroup.call(brush.move, null);
        return;
      }
      const range = accumulatedRanges[0];
      if (!range) {
        return;
      }
      const x0 = xScale(new Date(range.start_time));
      const x1 = xScale(new Date(range.end_time));
      if (!Number.isFinite(x0) || !Number.isFinite(x1)) {
        return;
      }
      brushGroup.call(brush.move, [Math.min(x0, x1), Math.max(x0, x1)] as [number, number]);
    } catch {
      /* noop — brush may not be mounted yet */
    } finally {
      isProgrammaticBrushMove = false;
    }
  }

  const publishBrushSelection = (
    eventIds: number[],
    hasSel: boolean,
    startTime: Date | null = null,
    endTime: Date | null = null
  ) => {
    notifyBrushSelect(eventIds, startTime, endTime);
    if (brushPublishTimer) {
      clearTimeout(brushPublishTimer);
    }
    brushPublishTimer = setTimeout(() => {
      if (props.skipSelectedSourcesFilter && eventIds.length > 0) {
        setLegendSelectionActive(false);
      }
      setDomainSelectedEvents(eventIds);
      setHasSelection(hasSel);
      setTriggerSelection(true);
      brushPublishTimer = null;
    }, 50);
  };

  const updateXAxisLabels = () => {
    if (!svg?.node() || !xScale) return;
    const chartBody = svg.select("g");
    if (chartBody.empty()) return;

    const isHistory = props.isHistoryPage || false;
    const tz = getTimezone();
    const timeFormatter = createTimeFormatter(tz, isHistory);
    const xAxis = d3.axisBottom(xScale)
      .ticks(5)
      .tickFormat((d) => {
        try {
          return timeFormatter(d) || '';
        } catch (error) {
          logDebug('ScatterTimeseries: Error in tickFormat', { error, date: d, timezone: tz });
          if (isHistory) {
            return d3.timeFormat("%Y-%m-%d")(d as Date);
          }
          return d3.timeFormat("%H:%M:%S")(d as Date);
        }
      });

    const xAxisGroup = chartBody.select<SVGGElement>("g.timeline-x-axis");
    if (xAxisGroup.empty()) return;

    xAxisGroup.call(xAxis);
    xAxisGroup.selectAll("path")
      .style("stroke", isDark() ? "white" : "black");
    xAxisGroup.selectAll("line")
      .style("stroke", isDark() ? "white" : "black");
    xAxisGroup.selectAll("text")
      .style("fill", isDark() ? "white" : "black")
      .style("font-size", "10px");
  };

  /** Match AdvancedScatter: dim timeline points when legend (or other UI) sets selectedEvents */
  const applyTimelineSelectionDimming = () => {
    // Maneuver strip: do not dim by selection — the maneuver pages drive their own colouring.
    if (!props.isHistoryPage || props.skipSelectedSourcesFilter || !svg?.node()) return;
    // Appearance, so the union. Now that the domains are separate this can no longer pick up stale
    // ids belonging to the other family, which is what the old comment here was guarding against.
    const sel = domainHighlightedIds();
    const hasSel = Array.isArray(sel) && sel.length > 0;
    const chartBody = svg.select("g");
    if (chartBody.empty()) return;
    chartBody.selectAll("circle.scatter-point").each(function (d: any) {
      const rawId = d?.event_id;
      if (rawId === undefined || rawId === null || rawId === '') {
        d3.select(this).style("opacity", hasSel ? 0.25 : 1);
        return;
      }
      const id = typeof rawId === 'string' ? parseInt(rawId, 10) : Number(rawId);
      const inSel = hasSel && !isNaN(id) && sel.includes(id);
      d3.select(this).style("opacity", hasSel ? (inSel ? 1 : 0.25) : 1);
    });
  };

  // Draw the chart
  const drawChart = () => {
    if (!containerRef) return;

    untrack(() => updateWidth());
    let currentWidth = width();
    if (currentWidth <= 0 && containerRef) {
      currentWidth = measureTimelineWidth(containerRef);
      if (currentWidth <= 0) {
        const media = containerRef.closest("#media-container");
        if (media instanceof HTMLElement) {
          currentWidth =
            media.clientWidth || Math.round(media.getBoundingClientRect().width);
        }
      }
      if (currentWidth <= 0) {
        const mc = document.getElementById("main-content");
        if (mc) {
          currentWidth = mc.clientWidth || Math.round(mc.getBoundingClientRect().width);
        }
      }
    }
    if (currentWidth <= 0) {
      const vw =
        typeof document !== "undefined"
          ? Math.max(
            document.documentElement?.clientWidth ?? 0,
            window.innerWidth ?? 0
          )
          : 0;
      if (vw > 0) {
        currentWidth = vw;
      }
    }
    if (currentWidth > 0 && currentWidth !== width()) {
      setWidth(currentWidth);
    }
    currentWidth = width() > 0 ? width() : currentWidth;
    const currentHeight = height();
    const chartWidth = currentWidth - margin.left - margin.right;
    const chartHeight = currentHeight - margin.top - margin.bottom;

    if (chartWidth <= 0 || chartHeight <= 0) {
      logDebug("ScatterTimeseries: drawChart skipped — non-positive chart size", {
        widthSignal: width(),
        currentWidth,
        chartWidth,
        chartHeight,
        hasContainerRef: !!containerRef,
      });
      return;
    }

    logDebug('ScatterTimeseries: drawChart called', {
      currentWidth,
      currentHeight,
      chartWidth,
      chartHeight,
      timestamp: new Date().toISOString(),
      callCount: drawChartCallCount + 1,
      msSinceMount: drawChartMountMs > 0 ? Math.round(performance.now() - drawChartMountMs) : 0,
    });

    drawDatetimeCache.clear();
    const processedData = processData(props.aggregates, drawDatetimeCache);
    lastProcessedData = processedData;
    refreshManeuverDimensionColorFn();
    if (processedData.length === 0) {
      lastProcessedData = [];
      const raw = props.aggregates;
      const sample = Array.isArray(raw) && raw.length > 0 ? (raw[0] as Record<string, unknown>) : undefined;
      logDebug("ScatterTimeseries: drawChart — no points after processData", {
        aggregateCount: Array.isArray(raw) ? raw.length : 0,
        skipSelectedSourcesFilter: !!props.skipSelectedSourcesFilter,
        sampleKeys: sample ? Object.keys(sample).slice(0, 36) : [],
      });
      d3.select(containerRef).selectAll("*").remove();
      svg = null;
      return;
    }

    const maneuverYAxisTws = !!props.skipSelectedSourcesFilter;
    const yAccessor = (d: ProcessedScatterPoint): number =>
      maneuverYAxisTws ? Number(d.TWS) : Number(d.BSP);

    const selectedForSample = untrack(() => domainSelectedEvents()) ?? [];
    let renderData = processedData;
    if (processedData.length > TIMELINE_MAX_RENDER_POINTS) {
      renderData = sampleTimelinePointsForRender(
        processedData,
        TIMELINE_MAX_RENDER_POINTS,
        yAccessor,
        selectedForSample
      );
      logDebug('ScatterTimeseries: timeline render sample', {
        input: processedData.length,
        output: renderData.length,
        maxPoints: TIMELINE_MAX_RENDER_POINTS,
        selectedCount: selectedForSample.length,
      });
    }

    drawChartCallCount += 1;
    logDebug('ScatterTimeseries: drawChart stats', {
      callCount: drawChartCallCount,
      msSinceMount: drawChartMountMs > 0 ? Math.round(performance.now() - drawChartMountMs) : 0,
      processedCount: processedData.length,
      renderedCount: renderData.length,
      performanceTimeline: isPerformanceTimeline(),
    });

    const timeExtent = d3.extent(renderData, d => d.datetime) as [Date | undefined, Date | undefined];
    const yExtentRaw = maneuverYAxisTws
      ? (d3.extent(renderData, d => d.TWS) as [number | undefined, number | undefined])
      : (d3.extent(renderData, d => d.BSP) as [number | undefined, number | undefined]);

    const badTime =
      !timeExtent[0] ||
      !timeExtent[1] ||
      !(timeExtent[0] instanceof Date) ||
      !(timeExtent[1] instanceof Date) ||
      Number.isNaN(timeExtent[0].getTime()) ||
      Number.isNaN(timeExtent[1].getTime());
    if (badTime) {
      const firstAgg = Array.isArray(props.aggregates) && props.aggregates.length > 0
        ? (props.aggregates[0] as Record<string, unknown>)
        : undefined;
      const firstParsed = firstAgg
        ? parseScatterDatetime(firstAgg, new Map())
        : null;
      logDebug('ScatterTimeseries: Invalid time extent', {
        timeExtent,
        processedDataLength: processedData.length,
        sampleData: processedData.slice(0, 3).map(d => ({
          t: d.datetime instanceof Date ? d.datetime.getTime() : d.datetime,
          event_id: d.event_id,
        })),
        firstAggregateKeys: firstAgg ? Object.keys(firstAgg).slice(0, 30) : [],
        firstAggregateParsed: firstParsed ? firstParsed.toISOString() : null,
      });
      d3.select(containerRef).selectAll("*").remove();
      svg = null;
      return;
    }

    if (
      yExtentRaw[0] === undefined ||
      yExtentRaw[1] === undefined ||
      Number.isNaN(yExtentRaw[0]) ||
      Number.isNaN(yExtentRaw[1])
    ) {
      logDebug(
        maneuverYAxisTws ? 'ScatterTimeseries: Invalid TWS extent' : 'ScatterTimeseries: Invalid BSP extent',
        { yExtentRaw, processedDataLength: processedData.length, maneuverYAxisTws },
      );
      d3.select(containerRef).selectAll("*").remove();
      svg = null;
      return;
    }

    let t0: Date = timeExtent[0]!;
    let t1: Date = timeExtent[1]!;

    d3.select(containerRef).selectAll("*").remove();

    svg = d3.select(containerRef)
      .append("svg")
      .attr("width", currentWidth)
      .attr("height", currentHeight);

    const chartBody = svg.append("g")
      .attr("transform", `translate(${margin.left}, ${margin.top})`);

    // d3.scaleTime requires a non-zero-width domain; single-day fleet data often collapses to one instant
    const spanMs = t1.getTime() - t0.getTime();
    if (spanMs === 0) {
      t0 = d3.timeMinute.offset(t0, -1);
      t1 = d3.timeMinute.offset(t1, 1);
    } else if (spanMs < 1000) {
      const midMs = (t0.getTime() + t1.getTime()) / 2;
      t0 = new Date(midMs - 1000);
      t1 = new Date(midMs + 1000);
    }
    // Pad time domain by 10% on each side so edge scatter points are not clipped
    const timeSpanMs = t1.getTime() - t0.getTime();
    const timePadMs = timeSpanMs * 0.1;
    t0 = new Date(t0.getTime() - timePadMs);
    t1 = new Date(t1.getTime() + timePadMs);

    const ymin = yExtentRaw[0] as number;
    const ymax = yExtentRaw[1] as number;
    let y0: number;
    let y1: number;
    if (maneuverYAxisTws) {
      const rawSpan = ymax - ymin;
      const span = rawSpan === 0 ? (Math.abs(ymax) < 1e-9 ? 1 : Math.abs(ymax) * 0.1) : rawSpan;
      y0 = ymin - 0.05 * span;
      y1 = ymax + 0.35 * span;
    } else {
      y0 = ymin;
      y1 = ymax;
      if (y0 === y1) {
        const pad = Math.abs(y0) < 1e-9 ? 0.5 : Math.max(1e-6, Math.abs(y0) * 0.05);
        y0 -= pad;
        y1 += pad;
      }
    }

    logDebug('ScatterTimeseries: Setting up scales', {
      timeExtent: [t0.toISOString(), t1.toISOString()],
      yDomain: [y0, y1],
      maneuverYAxisTws,
      chartWidth,
      chartHeight
    });

    xScale = d3.scaleTime()
      .domain([t0, t1])
      .range([0, chartWidth]);

    yScale = d3.scaleLinear()
      .domain([y0, y1])
      .range([chartHeight, 0]);

    // Draw axes
    // Format x-axis based on history page: dates for history, time for dataset
    // Use timezone-aware formatter (like TimeSeries does)
    const isHistory = props.isHistoryPage || false;
    const tz = getTimezone();
    lastTimezoneForAxis = tz ?? '';
    const timeFormatter = createTimeFormatter(tz, isHistory);

    const xAxis = d3.axisBottom(xScale)
      .ticks(5) // Add explicit tick count
      .tickFormat((d) => {
        try {
          const result = timeFormatter(d);
          return result || '';
        } catch (error) {
          logDebug('ScatterTimeseries: Error in tickFormat', { error, date: d, timezone: tz });
          // Fallback to simple D3 formatter
          if (isHistory) {
            return d3.timeFormat("%Y-%m-%d")(d as Date);
          }
          return d3.timeFormat("%H:%M:%S")(d as Date);
        }
      });

    const yAxis = d3.axisLeft(yScale)
      .ticks(3);

    // Draw x-axis (time axis)
    const xAxisGroup = chartBody.append("g")
      .attr("class", "timeline-x-axis")
      .attr("transform", `translate(0, ${chartHeight})`)
      .call(xAxis);

    // Style x-axis to be visible
    xAxisGroup.selectAll("path")
      .style("stroke", isDark() ? "white" : "black");
    xAxisGroup.selectAll("line")
      .style("stroke", isDark() ? "white" : "black");
    xAxisGroup.selectAll("text")
      .style("fill", isDark() ? "white" : "black")
      .style("font-size", "10px");

    // Draw y-axis
    const yAxisGroup = chartBody.append("g")
      .call(yAxis);

    // Style y-axis to be visible
    yAxisGroup.selectAll("path")
      .style("stroke", isDark() ? "white" : "black");
    yAxisGroup.selectAll("line")
      .style("stroke", isDark() ? "white" : "black");
    yAxisGroup.selectAll("text")
      .style("fill", isDark() ? "white" : "black")
      .style("font-size", "10px");

    // Y-axis label — TWS for maneuver selection strip, BSP for performance timelines
    const yChannelLabel = maneuverYAxisTws ? getTwsName() : getBspName();
    chartBody.append("text")
      .attr("x", 15)
      .attr("y", 15)
      .attr("text-anchor", "start")
      .attr("font-size", "12px")
      .style("fill", isDark() ? "white" : "black")
      .text(yChannelLabel);

    // Draw scatter points
    chartBody.selectAll("circle")
      .data(renderData)
      .enter()
      .append("circle")
      .attr("class", "scatter-point")
      .attr("cx", d => xScale!(d.datetime || d.Datetime))
      .attr("cy", d => yScale!(maneuverYAxisTws ? d.TWS : d.BSP))
      .attr("r", 2)
      .style("fill", d => getScatterPointColor(d as Record<string, unknown>, props.color, props.groups, props.colorScale))
      .style("stroke", d => getScatterPointColor(d as Record<string, unknown>, props.color, props.groups, props.colorScale));
    paintRampColors(chartBody.selectAll("circle.scatter-point"), props.color, false);

    // Bands already brushed with Ctrl held (drawn under the live brush rectangle)
    chartBody.append("g").attr("class", "brush-bands").style("pointer-events", "none");

    // Create brush. D3's default filter drops ctrlKey drags (macOS right-click emulation), so
    // Ctrl+drag would never start a brush — allow it and keep the rest of the default guard.
    brush = d3.brushX()
      .extent([[0, 0], [chartWidth, chartHeight]])
      .filter((event: MouseEvent) => !event.button)
      .on("start", brushStarted)
      .on("end", brushed);

    chartBody.append("g")
      .attr("class", "brush")
      .call(brush)
      // macOS reads Ctrl+click as a right-click; keep the menu out of an additive drag.
      .on("contextmenu", (event: MouseEvent) => {
        if (event.ctrlKey || event.metaKey) { event.preventDefault(); }
      });

    // The SVG was just rebuilt (resize/data change) — put the accumulated bands back on it.
    renderAccumulatedBrushBands();
    restoreBrushSelection();

    applyTimelineSelectionDimming();
    applyManeuverStripSelectionOpacity();
  };

  /** Record whether the drag began with Ctrl/Cmd held — that makes this brush additive. */
  function brushStarted(event: { sourceEvent?: MouseEvent } | undefined) {
    if (isProgrammaticBrushMove) {
      return;
    }
    const source = event?.sourceEvent;
    additiveGestureArmed = Boolean(source?.ctrlKey || source?.metaKey);
  }

  // Brush handler
  function brushed(event: any) {
    if (isProgrammaticBrushMove) {
      return;
    }
    // A rebuild (`brush.move(null)` / remount) has no pointer sourceEvent. Publishing an empty
    // selection from that path is what wiped a live-page manual pick whenever the strip remounted
    // (e.g. a key that included the growing row count). Only a real gesture may clear.
    if (!event?.sourceEvent) {
      return;
    }
    // Ctrl held at either end of the drag counts: the user may press or release it mid-drag.
    const source = event.sourceEvent as MouseEvent | undefined;
    const isAdditive = additiveGestureArmed || Boolean(source?.ctrlKey || source?.metaKey);
    additiveGestureArmed = false;

    if (!event || !event.selection || !xScale || lastProcessedData.length === 0) {
      // A Ctrl gesture that selected nothing must not wipe what is already selected.
      if (isAdditive && accumulatedEventIds.size > 0) {
        renderAccumulatedBrushBands();
        return;
      }
      accumulatedRanges = [];
      accumulatedEventIds = new Set<number>();
      renderAccumulatedBrushBands();
      publishBrushSelection([], false, null, null);
      return;
    }

    const [x0, x1] = event.selection.map((x: number) => xScale!.invert(x));
    const selectionDuration = Math.abs(x1.getTime() - x0.getTime());

    // Only update if selection duration is significant (avoid accidental clicks)
    if (selectionDuration < 1000) {
      return;
    }

    const startTime = x0 < x1 ? x0 : x1;
    const endTime = x0 < x1 ? x1 : x0;

    // Use last processed points (already parsed once during drawChart)
    const filteredData = lastProcessedData.filter((d) => {
      const datetime = d.datetime;
      return datetime >= startTime && datetime <= endTime;
    });

    // Extract unique event_ids
    const eventIds = new Set<number>();
    filteredData.forEach(d => {
      if (d.event_id !== undefined && d.event_id !== null && d.event_id !== '') {
        const parsed = typeof d.event_id === 'string' ? parseInt(d.event_id, 10) : Number(d.event_id);
        if (!isNaN(parsed)) {
          eventIds.add(parsed);
        }
      }
    });

    const eventIdsArray = Array.from(eventIds);

    if (isAdditive) {
      // Ctrl+drag: add this band to what is already selected instead of replacing it.
      if (eventIdsArray.length === 0) {
        // Nothing under the band — leave the existing selection untouched.
        renderAccumulatedBrushBands();
        return;
      }
      for (const id of eventIdsArray) {
        accumulatedEventIds.add(id);
      }
      accumulatedRanges = addScatterBrushRange(accumulatedRanges, {
        start_time: startTime.toISOString(),
        end_time: endTime.toISOString(),
      });
      publishBrushSelection(Array.from(accumulatedEventIds), true, startTime, endTime);
      // The band may have merged with a neighbour: re-seat the rectangle on what is actually
      // selected (or drop it and paint every band once there is more than one).
      restoreBrushSelection();
      renderAccumulatedBrushBands();
      logDebug('ScatterTimeseries: Brush selection added to selectedEvents (Ctrl)', {
        addedCount: eventIdsArray.length,
        totalCount: accumulatedEventIds.size,
        bandCount: accumulatedRanges.length,
        timeRange: { start: startTime.toISOString(), end: endTime.toISOString() }
      });
      return;
    }

    // Plain drag replaces every accumulated band.
    accumulatedRanges = [{ start_time: startTime.toISOString(), end_time: endTime.toISOString() }];
    accumulatedEventIds = new Set<number>(eventIdsArray);
    renderAccumulatedBrushBands();

    if (eventIdsArray.length > 0) {
      publishBrushSelection(eventIdsArray, true, startTime, endTime);
      logDebug('ScatterTimeseries: Brush selection updated selectedEvents', {
        eventCount: eventIdsArray.length,
        timeRange: { start: startTime.toISOString(), end: endTime.toISOString() }
      });
    } else {
      publishBrushSelection([], false, startTime, endTime);
    }
  }

  // Update colors only (preserves scales and chart structure)
  const updateColors = () => {
    if (!containerRef || !svg) return;
    const chartBody = svg.select("g");
    if (chartBody.empty()) return;

    refreshManeuverDimensionColorFn();

    // Use untrack to get current props without creating reactive dependencies
    const currentColor = untrack(() => props.color);
    const currentGroups = untrack(() => props.groups);
    const currentColorScale = untrack(() => props.colorScale);

    if (paintRampColors(chartBody.selectAll("circle.scatter-point"), currentColor, true)) {
      if (untrack(() => props.isHistoryPage) && !untrack(() => props.skipSelectedSourcesFilter)) {
        applyTimelineSelectionDimming();
      }
      return;
    }

    chartBody.selectAll("circle.scatter-point")
      .transition()
      .duration(200)
      .style("fill", (d: Record<string, unknown>) => getScatterPointColor(d, currentColor, currentGroups, currentColorScale))
      .style("stroke", (d: Record<string, unknown>) => getScatterPointColor(d, currentColor, currentGroups, currentColorScale))
      .on('end', () => {
        if (untrack(() => props.skipSelectedSourcesFilter)) {
          applyManeuverStripSelectionOpacity();
        } else if (untrack(() => props.isHistoryPage)) {
          applyTimelineSelectionDimming();
        }
      });
  };

  // Same as performance scatter (AdvancedScatter): use our container's width so we scale with media-container
  // Legend uses fixed 1500px + parent transform; AdvancedScatter uses chartRef.clientWidth. We use containerRef.clientWidth.
  const measureTimelineWidth = (el: HTMLElement): number => {
    let w = el.clientWidth || 0;
    if (w === 0 && el.parentElement) {
      w = el.parentElement.clientWidth || 0;
    }
    if (w === 0) {
      const r = el.getBoundingClientRect();
      w = Math.max(0, Math.round(r.width));
    }
    return w;
  };

  const scheduleUpdateWidth = () => {
    if (widthRafId != null) return;
    widthRafId = requestAnimationFrame(() => {
      widthRafId = null;
      updateWidth();
    });
  };

  const updateWidth = () => {
    let w = 0;
    if (containerRef) {
      w = measureTimelineWidth(containerRef);
      if (w === 0 && !isPerformanceTimeline()) {
        const media = containerRef.closest("#media-container");
        if (media instanceof HTMLElement) {
          w = media.clientWidth || Math.round(media.getBoundingClientRect().width);
        }
      }
    }
    if (w > 0) {
      if (w !== width()) setWidth(w);
      return;
    }
    if (!isPerformanceTimeline()) {
      const mainContent = document.getElementById("main-content");
      if (mainContent) {
        const mcw =
          mainContent.clientWidth || Math.round(mainContent.getBoundingClientRect().width);
        if (mcw > 0) {
          if (mcw !== width()) setWidth(mcw);
          return;
        }
      }
      const docMedia = document.getElementById("media-container");
      if (docMedia) {
        const dw = docMedia.clientWidth || Math.round(docMedia.getBoundingClientRect().width);
        if (dw > 0) {
          if (dw !== width()) setWidth(dw);
          return;
        }
      }
      const vw =
        typeof document !== "undefined"
          ? Math.max(
            document.documentElement?.clientWidth ?? 0,
            window.innerWidth ?? 0
          )
          : 0;
      if (vw > 0 && vw !== width()) setWidth(vw);
    }
  };

  // Initialize width on mount — performance timelines observe container + parent only
  let resizeObservedMedia: HTMLElement | null = null;
  onMount(() => {
    drawChartMountMs = performance.now();
    const perfTimeline = isPerformanceTimeline();
    resizeObserver = new ResizeObserver(() => scheduleUpdateWidth());
    if (containerRef) {
      resizeObserver.observe(containerRef);
      const parent = containerRef.parentElement;
      if (parent) {
        resizeObservedParent = parent;
        resizeObserver.observe(parent);
      }
      if (!perfTimeline) {
        resizeObservedMedia = containerRef.closest('#media-container');
        if (resizeObservedMedia instanceof HTMLElement) {
          resizeObserver.observe(resizeObservedMedia);
        }
        const mainContent = document.getElementById('main-content');
        if (mainContent) {
          resizeObserver.observe(mainContent);
        }
        window.addEventListener("resize", scheduleUpdateWidth);
      }
      scheduleUpdateWidth();
    }

    // Deferred measure after layout (handles mount inside Show; media-container may not be sized yet)
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        scheduleUpdateWidth();
      });
    });

    {
      const onSelectionBannerCleared = () => {
        // Drop the accumulated Ctrl+drag bands on every path; only the callback-driven strips also
        // clear the D3 rectangle, as they did before.
        resetAccumulatedBrushBands();
        if (props.onBrushSelect) {
          clearBrushSelectionVisual();
        }
      };
      window.addEventListener("selection-banner-cleared", onSelectionBannerCleared);
      onCleanup(() => {
        window.removeEventListener("selection-banner-cleared", onSelectionBannerCleared);
      });
    }

    // Initialize dataset timezone (non-reactive, fire and forget - don't await)
    // This ensures timezone is available for future renders without triggering a redraw
    const className = selectedClassName();
    const projectId = selectedProjectId();
    let datasetId: number | null = null;

    if (typeof selectedDatasetId === 'function') {
      const dsId = selectedDatasetId();
      datasetId = dsId ? Number(dsId) : null;
    } else if (selectedDatasetId) {
      datasetId = Number(selectedDatasetId);
    }

    if (className && projectId && datasetId && datasetId > 0) {
      // Fire and forget - don't await to avoid timing issues
      setCurrentDataset(className, projectId, datasetId).catch(() => {
        // Silently ignore errors - timezone is optional
      });
    }
  });

  // Effect 1: Initial draw and data changes (filters, uwDw, aggregates) - debounced like AdvancedScatter
  createEffect(() => {
    // Track uwDw and showUwDw to make effect reactive to filter changes
    const uwDw = props.uwDw || '';
    const showUwDw = props.showUwDw || false;

    const skipSelectedSourcesFilter = !!props.skipSelectedSourcesFilter;
    // Track selected sources for redraw when store filter changes (skipped for maneuver timelines)
    const selected = skipSelectedSourcesFilter ? [] : selectedSources();
    const selectedSourcesHash =
      selected.length > 0 ? selected.sort().join(',') : skipSelectedSourcesFilter ? 'skip' : '';

    // Track aggregates so effect re-runs when perf filters (year/event/config/state/grade/race/leg) change
    const aggregates = props.aggregates;
    const isHistoryPage = untrack(() => props.isHistoryPage);

    // Build simple data signature (avoid expensive processData call)
    const aggregatesLength = Array.isArray(aggregates) ? aggregates.length : 0;

    // Simple time hash: first/last datetime (reuse per-draw cache for signature only)
    let timeHash = '';
    if (aggregates && aggregatesLength > 0) {
      const signatureCache: ScatterDatetimeCache = new Map();
      const first = aggregates[0];
      const last = aggregates[aggregatesLength - 1];

      const firstDt = parseScatterDatetime(
        first as Record<string, unknown>,
        signatureCache
      );
      const lastDt = parseScatterDatetime(
        last as Record<string, unknown>,
        signatureCache
      );
      if (firstDt && lastDt) {
        timeHash = `${firstDt.getTime()}-${lastDt.getTime()}`;
      }
    }

    // Always refresh width hints; performance timelines use coalesced container sizing only.
    if (!isPerformanceTimeline()) {
      untrack(() => scheduleUpdateWidth());
    }

    // Include uwDw and selectedSources in signature so filtering changes trigger redraw
    const currentDataSignature = `${aggregatesLength}|${isHistoryPage ? '1' : '0'}|${timeHash}|${showUwDw ? uwDw : ''}|${selectedSourcesHash}`;

    // Check if chart exists
    const chartExists = svg && svg.node();

    // If signature changed OR chart doesn't exist yet (initial draw), redraw
    if (currentDataSignature !== lastDataSignature || !chartExists) {
      lastDataSignature = currentDataSignature;

      // Debounce data redraws (like AdvancedScatter)
      if (dataRedrawTimer) {
        clearTimeout(dataRedrawTimer);
      }

      dataRedrawTimer = setTimeout(() => {
        if (!containerRef) {
          dataRedrawTimer = null;
          return;
        }

        const aggregatesValue = untrack(() => props.aggregates);

        requestAnimationFrame(() => {
          // Draw if we have data (chart may or may not exist yet)
          if (aggregatesValue && Array.isArray(aggregatesValue) && aggregatesValue.length > 0) {
            drawChart();
          }
          dataRedrawTimer = null;
        });
      }, chartExists ? 200 : 0); // No debounce for initial draw
    }
  });

  createEffect(() => {
    if (!props.skipSelectedSourcesFilter) {
      if (!props.isHistoryPage) return;
      domainSelectedEvents();
      applyTimelineSelectionDimming();
      return;
    }
    domainSelectedEvents();
    legendSelectionActive();
    if (svg?.node()) {
      updateColors();
    }
  });

  /**
   * The selection was cleared from elsewhere (banner, another chart, a page action) — drop the
   * accumulated Ctrl+drag bands so the next additive drag starts fresh instead of appending to them.
   * Skipped while a publish is in flight: `publishBrushSelection` debounces by 50ms, so during that
   * window `domainSelectedEvents()` is still the pre-drag value and would otherwise wipe the bands we just set.
   */
  createEffect(() => {
    const sel = domainSelectedEvents();
    const isEmpty = !Array.isArray(sel) || sel.length === 0;
    untrack(() => {
      if (isEmpty && brushPublishTimer === null && accumulatedEventIds.size > 0) {
        resetAccumulatedBrushBands();
      }
    });
  });

  // Effect 2: Color/groups changes - only update colors, don't redraw
  createEffect(() => {
    // Track color, groups, and colorScale to make effect reactive
    void props.color;
    void props.groups;
    void props.colorScale;

    if (!containerRef) return;

    // Use untrack to get current values without creating additional reactive dependencies
    const currentColor = untrack(() => props.color);
    const currentGroups = untrack(() => props.groups);
    const currentColorScale = untrack(() => props.colorScale);
    const aggregatesLength = untrack(() => Array.isArray(props.aggregates) ? props.aggregates.length : 0);

    const groupsSignature = currentGroups ? currentGroups.map((g: any) => `${g.name}:${g.color}`).join('|') : '';
    const colorScaleExists = currentColorScale && typeof currentColorScale === 'function' ? '1' : '0';
    const currentColorGroupsSignature = `${currentColor || ''}|${groupsSignature}|${aggregatesLength}|${colorScaleExists}`;

    if (currentColorGroupsSignature === lastColorGroupsSignature) {
      return; // No color change, skip update
    }

    lastColorGroupsSignature = currentColorGroupsSignature;

    // Check if chart exists using untrack to avoid reactive dependency
    const chartExists = untrack(() => svg?.node());
    if (chartExists) {
      updateColors();
    } else if (width() > 0) {
      // Do not draw with empty aggregates — Effect 1 debounces the real data draw; an early draw here
      // cleared the strip and left aggregateCount 0 (fleet day loaded map rows before table mapped points).
      const n = untrack(() =>
        Array.isArray(props.aggregates) ? props.aggregates.length : 0
      );
      if (n > 0) {
        drawChart();
      }
    }
  });

  // Effect 3: Width changes (resize) - debounced redraw when width changes
  let lastWidthForResize = 0;
  createEffect(() => {
    const currentWidth = width();

    if (currentWidth !== lastWidthForResize && currentWidth > 0) {
      lastWidthForResize = currentWidth;

      if (resizeRedrawTimer) {
        clearTimeout(resizeRedrawTimer);
      }

      resizeRedrawTimer = setTimeout(() => {
        if (svg && svg.node()) {
          drawChart();
        } else {
          const n = untrack(() =>
            Array.isArray(props.aggregates) ? props.aggregates.length : 0
          );
          if (n > 0 && containerRef) {
            drawChart();
          }
        }
        resizeRedrawTimer = null;
      }, 200);
    }
  });

  // Effect 4: Dataset timezone resolves/changes — refresh axis labels only (data instants are UTC).
  createEffect(() => {
    const tz = getCurrentDatasetTimezone();
    const tzKey = tz ?? '';
    if (tzKey === lastTimezoneForAxis) return;
    if (!svg?.node() || !xScale) return;
    const n = untrack(() =>
      Array.isArray(props.aggregates) ? props.aggregates.length : 0
    );
    if (n > 0) {
      lastTimezoneForAxis = tzKey;
      updateXAxisLabels();
      logDebug('ScatterTimeseries: timezone axis labels updated', { timezone: tzKey });
    }
  });

  // Cleanup
  onCleanup(() => {
    if (dataRedrawTimer) {
      clearTimeout(dataRedrawTimer);
      dataRedrawTimer = null;
    }
    if (resizeRedrawTimer) {
      clearTimeout(resizeRedrawTimer);
      resizeRedrawTimer = null;
    }

    if (brushPublishTimer) {
      clearTimeout(brushPublishTimer);
      brushPublishTimer = null;
    }
    if (widthRafId != null) {
      cancelAnimationFrame(widthRafId);
      widthRafId = null;
    }

    if (!isPerformanceTimeline()) {
      window.removeEventListener("resize", scheduleUpdateWidth);
    }

    if (resizeObserver) {
      if (resizeObservedParent) {
        resizeObserver.unobserve(resizeObservedParent);
        resizeObservedParent = null;
      }
      if (containerRef) {
        resizeObserver.unobserve(containerRef);
      }
      if (resizeObservedMedia) {
        resizeObserver.unobserve(resizeObservedMedia);
        resizeObservedMedia = null;
      }
      resizeObserver.disconnect();
    }

    if (containerRef) {
      d3.select(containerRef).selectAll("*").remove();
    }

    svg = null;
    xScale = null;
    yScale = null;
    brush = null;
  });

  return (
    <div
      ref={(el) => (containerRef = el)}
      class="scatter-timeseries-root"
    />
  );
}
